import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { localDateStr } from "./entregasHelpers";

const POLL_MS = 20000;          // polling incremental (só docs alterados desde a última sync)
const FULL_SYNC_EVERY = 6;      // a cada N polls faz reload completo (segurança contra drift)

/** Converte o estado de filtros da tela em query params da API (Iter 94: filtro server-side; Iter 116: listas → "a,b"). */
export function buildEntregasParams(filters) {
  const p = {};
  if (filters.start) p.start = filters.start;
  if (filters.end) p.end = filters.end;
  for (const k of ["status", "entregador_id", "bairro_id", "rota_id", "tipo_id", "forma_pgto_id", "regiao_id"]) {
    const v = filters[k];
    const arr = Array.isArray(v) ? v : (v && v !== "all" ? [v] : []);
    if (arr.length) p[k] = arr.join(",");
  }
  for (const k of ["numero_pedido", "cliente_nome", "cliente_telefone", "valor_min", "valor_max"]) {
    if (filters[k] !== "" && filters[k] != null) p[k] = filters[k];
  }
  return p;
}

/**
 * useEntregasData — Hook central de dados da tela de Entregas.
 *
 * Iter 94 (set/2026 — performance):
 *  - Filtros (período, status, entregador, bairro, texto, valor) e paginação
 *    (`page`/`pageSize`) vão direto pra API → nada é filtrado em memória.
 *  - Polling de 20s é INCREMENTAL: envia `updated_since` e recebe só docs novos/alterados.
 *    Se o `total` do servidor divergir do conhecido (inserção/remoção/mudança de filtro),
 *    faz reload completo da página atual. Reload completo também a cada 6 polls.
 *  - Catálogos (entregadores/bairros/...) carregam 1x por empresa.
 */
export function useEntregasData(filters, { page = 1, pageSize = 100, sort = null } = {}) {
  const { currentEmpresaId } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [programadasCount, setProgramadasCount] = useState(0);
  const [entregadores, setEnt] = useState([]);
  const [bairros, setBai] = useState([]);
  const [regioes, setReg] = useState([]);
  const [tipos, setTip] = useState([]);
  const [formas, setFpg] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [empresa, setEmpresa] = useState(null);

  const lastSyncRef = useRef(null);
  const totalRef = useRef(0);
  const tickRef = useRef(0);
  const paramsRef = useRef({ filters, page, pageSize, sort });
  paramsRef.current = { filters, page, pageSize, sort };

  const loadCatalogs = useCallback(async () => {
    const [ent, bai, reg, tip, fpg, emp, rot] = await Promise.all([
      api.get("/entregadores"), api.get("/bairros"), api.get("/regioes"),
      api.get("/tipos-entrega"), api.get("/formas-pgto"),
      api.get("/empresa").catch(() => ({ data: null })),
      api.get("/rotas").catch(() => ({ data: [] })),
    ]);
    setEnt(ent.data); setBai(bai.data); setReg(reg.data); setTip(tip.data); setFpg(fpg.data); setEmpresa(emp.data);
    setRotas(Array.isArray(rot.data) ? rot.data : []);
  }, []);

  const loadProgramadas = useCallback(async () => {
    const tomorrow = localDateStr(new Date(Date.now() + 86400000));
    try {
      const r = await api.get("/entregas", { params: { start: tomorrow, limit: 1 } });
      setProgramadasCount(r.data.total || 0);
    } catch (err) { console.debug("programadas: contador opcional falhou", err?.message); }
  }, []);

  // Reload completo da página atual (respeitando filtros).
  const loadEntregas = useCallback(async () => {
    const { filters: f, page: pg, pageSize: ps, sort: so } = paramsRef.current;
    setLoading(true);
    try {
      const sortParams = so?.by ? { sort_by: so.by, sort_dir: so.dir || "desc" } : {};
      const r = await api.get("/entregas", { params: { ...buildEntregasParams(f), ...sortParams, limit: ps, skip: (pg - 1) * ps } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      totalRef.current = r.data.total || 0;
      lastSyncRef.current = r.data.server_time || null;
    } finally {
      setLoading(false);
    }
    loadProgramadas();
  }, [loadProgramadas]);

  // Polling incremental: só o que mudou desde `lastSync`. Reload completo se o total divergir.
  const syncIncremental = useCallback(async () => {
    tickRef.current += 1;
    if (!lastSyncRef.current || tickRef.current % FULL_SYNC_EVERY === 0) return loadEntregas();
    const { filters: f } = paramsRef.current;
    try {
      const r = await api.get("/entregas", { params: { ...buildEntregasParams(f), updated_since: lastSyncRef.current, limit: 500 } });
      const changed = r.data.items || [];
      if ((r.data.total || 0) !== totalRef.current) return loadEntregas();
      lastSyncRef.current = r.data.server_time || lastSyncRef.current;
      if (changed.length === 0) return;
      const byId = new Map(changed.map((c) => [c.id, c]));
      setItems((prev) => prev.map((it) => byId.get(it.id) || it));
    } catch (err) { console.debug("refresh de status falhou; próximo tick tenta de novo", err?.message); }
  }, [loadEntregas]);

  // Compat: `load()` continua sendo o "recarregue tudo" chamado após CRUD.
  const load = useCallback(() => loadEntregas(), [loadEntregas]);

  useEffect(() => { loadCatalogs(); }, [currentEmpresaId, loadCatalogs]);

  // Debounce nos filtros de texto (350ms) + reload imediato em page/pageSize/sort/empresa.
  const filtersKey = JSON.stringify(filters);
  const sortKey = sort ? `${sort.by}:${sort.dir}` : "";
  useEffect(() => {
    const t = setTimeout(loadEntregas, 350);
    return () => clearTimeout(t);
  }, [filtersKey, page, pageSize, sortKey, currentEmpresaId, loadEntregas]);

  useEffect(() => {
    const t = setInterval(syncIncremental, POLL_MS);
    return () => clearInterval(t);
  }, [syncIncremental]);

  useEffect(() => {
    const h = () => { loadCatalogs(); loadEntregas(); };
    const h2 = () => loadEntregas();
    window.addEventListener("empresa-changed", h);
    window.addEventListener("entregas-changed", h2);
    return () => {
      window.removeEventListener("empresa-changed", h);
      window.removeEventListener("entregas-changed", h2);
    };
  }, [loadCatalogs, loadEntregas]);

  // Callback pro SelectWithAdd — atualiza catálogo local depois de Quick-Add sem re-fetch.
  const onCatalogAdd = (kind, doc) => {
    const setters = { bairros: setBai, regioes: setReg, tipos_entrega: setTip, formas_pgto: setFpg };
    const setter = setters[kind];
    if (setter) setter((prev) => [...prev, doc].sort((a, b) => (a.nome || "").localeCompare(b.nome || "")));
  };

  // Iter 100: bulk-assign-rota aprende vínculos bairro→rota — recarrega só o catálogo de rotas.
  const reloadRotas = useCallback(() => api.get("/rotas").then((r) => setRotas(Array.isArray(r.data) ? r.data : [])).catch(() => {}), []);

  return {
    items, total, loading, programadasCount,
    entregadores, bairros, regioes, tipos, formas, rotas, empresa,
    setEmpresa, load, onCatalogAdd, reloadRotas,
  };
}
